import type { IMergedFloorView, IMinimizedProfession, IProfession } from '@d-orca/shared/trusstor';
import type { FloorWithChips } from '@store/selectors/monitor.selectors';
import { professionColor } from '@utils/professionColor';

export type TagRowDetails = {
  tagId: string;
  /** Worker's name from the merged tag's `name` field (only on worker
   *  tags; manager / utility tags don't carry one). Admin-only. */
  name: string | undefined;
  professionLabel: string | undefined;
  batteryLevel: string | undefined;
  timestamp: string | undefined;
  status: string | undefined;
};

export type FloorTradeRow = {
  professionId: string;
  tradeId: string;
  tradeGroup: string | undefined;
  color: string;
  tags: TagRowDetails[];
};

/** Per-floor trade rows (label, color, list of tag ids). Building the list
 *  from the raw `floors` payload — `selectFloorChips` only carries counts,
 *  not the underlying tag identifiers needed by the hover popover. */
export function buildTradesByFloorId(
  floors: IMergedFloorView<IMinimizedProfession>[],
  professions: IProfession[],
  selectedProfIds: string[],
): Map<string, FloorTradeRow[]> {
  const profById = new Map(professions.map((p) => [p._id, p]));
  // Mirror `selectFloorChips`'s profession filter so the popover trade
  // list narrows in lockstep with the slab chips when the user toggles
  // checkboxes in the LeftRail.
  const profFilter = selectedProfIds.length > 0 ? new Set(selectedProfIds) : null;
  const m = new Map<string, FloorTradeRow[]>();
  for (const f of floors) {
    // Index the floor's full tag list (worker / manager / utility tags
    // merged) by tagId so we can hydrate per-tag details — battery,
    // status, timestamp, full profession label — for the admin-only
    // hover popover. Trusstor's snapshot includes these on every tag.
    const tagsByTagId = new Map<string, Record<string, unknown>>();
    const rawTags = (f as { tags?: unknown }).tags;
    if (Array.isArray(rawTags)) {
      for (const t of rawTags as Array<Record<string, unknown>>) {
        if (typeof t.tagId === 'string') tagsByTagId.set(t.tagId, t);
      }
    }
    // Same trade-aggregation as `selectFloorChips` — collapse profession
    // variants into one row keyed by tradeId (specific trade), with the
    // underlying tag list unioned so the popover shows every tag under
    // that trade. Label is resolved at render via `tradeLabel(t, tradeId)`.
    const byTrade = new Map<string, FloorTradeRow>();
    for (const p of f.activeProfessions) {
      if (p.activeTagsIds.length === 0) continue;
      if (profFilter && !profFilter.has(p._id)) continue;
      const full = profById.get(p._id);
      const tradeId = full?.tradeId ?? p.tradeId;
      if (!tradeId) continue;
      const tradeGroup = full?.tradeGroup ?? p.tradeGroup;
      const color = professionColor(full?.backgroundColor);
      const tagDetails: TagRowDetails[] = p.activeTagsIds.map((id) => {
        const tag = tagsByTagId.get(id);
        const profession = tag?.profession as { tradeGroup?: string; specialty?: string } | undefined;
        const ts = tag?.timestamp;
        let timestamp: string | undefined;
        if (ts instanceof Date) timestamp = ts.toISOString();
        else if (typeof ts === 'string') timestamp = ts;
        return {
          tagId: id,
          name: typeof tag?.name === 'string' ? tag.name : undefined,
          professionLabel: profession?.tradeGroup ?? tradeGroup,
          batteryLevel: typeof tag?.batteryLevel === 'string' ? tag.batteryLevel : undefined,
          timestamp,
          status: typeof tag?.tagStatus === 'string' ? tag.tagStatus : undefined,
        };
      });
      const existing = byTrade.get(tradeId);
      if (existing) {
        existing.tags.push(...tagDetails);
      } else {
        byTrade.set(tradeId, {
          professionId: p._id,
          tradeId,
          tradeGroup,
          color,
          tags: tagDetails,
        });
      }
    }
    if (byTrade.size > 0) {
      m.set(
        String(f.floorId),
        Array.from(byTrade.values()).sort((a, b) => b.tags.length - a.tags.length),
      );
    }
  }
  return m;
}

/** Replay's per-floor trade rows, from the frame's chips. A past minute has no
 *  live tag list, so the rows carry no tags (the admin-only per-tag lines). */
export function tradesFromChips(chipsByFloor: ReadonlyMap<string, FloorWithChips>): Map<string, FloorTradeRow[]> {
  return new Map(
    [...chipsByFloor].map(([floorId, floor]) => [
      floorId,
      floor.chips.map((chip) => ({ professionId: chip.professionId, tradeId: chip.tradeId, tradeGroup: chip.tradeGroup, color: chip.color, tags: [] })),
    ]),
  );
}
